import React from 'react';
import { motion } from 'framer-motion';
import { GraduationCap, Award } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
export function MasterClassSection() {
  const {
    t,
    language
  } = useLanguage();
  const flyers = [{
    src: 'https://cdn.magicpatterns.com/uploads/w6e1fguypjyPn3WXrMEVAb/lore2.jpg',
    alt: 'Calendario Master Class',
    title: language === 'es' ? 'Calendario Master Class' : 'Master Class Calendar'
  }, {
    src: 'https://cdn.magicpatterns.com/uploads/oBrJyJ7AfsXdQt4BgN7ceH/lore6.jpg',
    alt: 'Estética Corporal',
    title: language === 'es' ? 'Estética Corporal' : 'Body Aesthetics'
  }, {
    src: 'https://cdn.magicpatterns.com/uploads/d3Ua9EkBFicHKj4ovBub69/lore5.jpg',
    alt: '5 Técnicas de Láser',
    title: language === 'es' ? '5 Técnicas de Láser' : '5 Laser Techniques'
  }, {
    src: 'https://cdn.magicpatterns.com/uploads/aLzDDMcLpGsqXcUC9R5ZMD/lore1.jpg',
    alt: 'Peeling Corporal y Zonas Íntimas',
    title: language === 'es' ? 'Peeling Corporal y Zonas Íntimas' : 'Body & Intimate Peeling'
  }];
  const galleryImages = [{
    src: 'https://cdn.magicpatterns.com/uploads/3Ciy9izwsWWr9uLMegooec/lore_master_class.jpg',
    alt: 'Lorena Master Class — Graduadas con certificados'
  }, {
    src: 'https://cdn.magicpatterns.com/uploads/cS3rU2yyiSeoZctoN8tD7t/lor_emaster_class_2.jpg',
    alt: 'Lorena Master Class — Alumnas certificadas'
  }, {
    src: 'https://cdn.magicpatterns.com/uploads/now785CfL7DdC3ZhBFKzT3/lore_master_class5.jpg',
    alt: 'Lorena Master Class — Sesiones de formación'
  }];
  return <section id="formacion" className="pt-4 pb-20 bg-beige-light" data-id="element-256">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-257">
        {/* Hero Banner */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-100px'
      }} transition={{
        duration: 0.6
      }} className="text-center mb-10" data-id="element-258">
          <div className="flex items-center justify-center space-x-3 mb-4" data-id="element-259">
            <GraduationCap className="w-5 h-5 text-gold" data-id="element-260" />
            <span className="text-sm font-medium text-gold uppercase tracking-widest" data-id="element-261">
              {t('masterclass', 'badge')}
            </span>
          </div>
          <h2 className="text-3xl md:text-4xl font-serif font-bold text-dark mb-6" data-id="element-262">
            {t('masterclass', 'title')}
          </h2>
          <div className="w-24 h-1 bg-gold mx-auto mb-6" data-id="element-263"></div>
          <p className="text-lg text-dark/70 max-w-2xl mx-auto leading-relaxed" data-id="element-264">
            {t('masterclass', 'desc')}
          </p>
        </motion.div>

        {/* Highlights */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="flex flex-wrap justify-center gap-4 mb-12" data-id="element-265">
          {['highlight1', 'highlight2', 'highlight3', 'highlight4'].map((key, i) => <div key={i} className="flex items-center space-x-2 bg-white px-5 py-3 rounded-full border border-gold/20 shadow-sm" data-id="element-266">
                <Award className="w-4 h-4 text-gold" data-id="element-267" />
                <span className="text-sm font-medium text-dark" data-id="element-268">
                  {t('masterclass', key)}
                </span>
              </div>)}
        </motion.div>

        {/* Promotional Flyers Grid (Replaces old hero and 2 flyers) */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12 mb-16 px-4 sm:px-0" data-id="element-269">
          {flyers.map((flyer, index) => <motion.div key={index} initial={{
          opacity: 0,
          scale: 0.95
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true,
          margin: '-50px'
        }} transition={{
          duration: 0.7,
          delay: index * 0.15
        }} className="relative group mx-auto w-full max-w-lg" data-id="element-270">
              {/* Decorative gold frame */}
              <div className="absolute -inset-3 border-2 border-gold/30 rounded-lg" data-id="element-271"></div>
              <div className="absolute -inset-1.5 border border-gold/15 rounded-md" data-id="element-272"></div>

              <div className="relative overflow-hidden rounded-md shadow-2xl bg-beige-light flex justify-center" data-id="element-273">
                <img src={flyer.src} alt={flyer.alt} className="w-full h-auto object-contain" data-id="element-274" />
                {/* Overlay Label */}
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent p-6 pt-12 opacity-0 group-hover:opacity-100 transition-opacity duration-500" data-id="element-275">
                  <p className="text-white font-serif text-lg font-medium tracking-wide text-center drop-shadow-lg" data-id="element-276">
                    {flyer.title}
                  </p>
                </div>
                {/* Subtle gold shimmer overlay on hover */}
                <div className="absolute inset-0 bg-gradient-to-tr from-gold/0 via-gold/5 to-gold/0 opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none" data-id="element-277"></div>
              </div>

              {/* Gold corner accents */}
              <div className="absolute -top-3 -left-3 w-8 h-8 border-t-2 border-l-2 border-gold/0 group-hover:border-gold transition-all duration-500 rounded-tl-lg" data-id="element-278"></div>
              <div className="absolute -top-3 -right-3 w-8 h-8 border-t-2 border-r-2 border-gold/0 group-hover:border-gold transition-all duration-500 rounded-tr-lg" data-id="element-279"></div>
              <div className="absolute -bottom-3 -left-3 w-8 h-8 border-b-2 border-l-2 border-gold/0 group-hover:border-gold transition-all duration-500 rounded-bl-lg" data-id="element-280"></div>
              <div className="absolute -bottom-3 -right-3 w-8 h-8 border-b-2 border-r-2 border-gold/0 group-hover:border-gold transition-all duration-500 rounded-br-lg" data-id="element-281"></div>
            </motion.div>)}
        </div>

        {/* Gallery Grid — same style as Facilities */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5" data-id="element-282">
          {galleryImages.map((image, index) => <motion.div key={index} initial={{
          opacity: 0,
          y: 40
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-50px'
        }} transition={{
          duration: 0.6,
          delay: index * 0.15
        }} className="relative overflow-hidden rounded-md group aspect-square border-2 border-gold/20 shadow-md hover:shadow-xl hover:border-gold/50 transition-all duration-500" data-id="element-283">
              <img src={image.src} alt={image.alt} className="w-full h-full object-cover transition-transform duration-1000 ease-out group-hover:scale-110" data-id="element-284" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" data-id="element-285"></div>
              {/* Gold corner accents */}
              <div className="absolute top-0 left-0 w-6 h-6 border-t-2 border-l-2 border-gold/0 group-hover:border-gold transition-all duration-500 rounded-tl-md" data-id="element-286"></div>
              <div className="absolute top-0 right-0 w-6 h-6 border-t-2 border-r-2 border-gold/0 group-hover:border-gold transition-all duration-500 rounded-tr-md" data-id="element-287"></div>
              <div className="absolute bottom-0 left-0 w-6 h-6 border-b-2 border-l-2 border-gold/0 group-hover:border-gold transition-all duration-500 rounded-bl-md" data-id="element-288"></div>
              <div className="absolute bottom-0 right-0 w-6 h-6 border-b-2 border-r-2 border-gold/0 group-hover:border-gold transition-all duration-500 rounded-br-md" data-id="element-289"></div>
            </motion.div>)}
        </div>

        <motion.div initial={{
        opacity: 0,
        y: 10
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6,
        delay: 0.5
      }} className="text-center mt-10" data-id="element-290">
          <p className="text-gold-dark font-serif text-xl font-medium italic mb-6" data-id="element-291">
            {t('masterclass', 'cta')}
          </p>
          <a href="https://book.stripe.com/14A3cucxT2QIgIWdwB3cc01" target="_blank" rel="noopener noreferrer" className="inline-block bg-gold hover:bg-gold-dark text-white px-10 py-4 rounded-sm font-bold text-lg transition-colors shadow-lg uppercase tracking-wide" data-id="element-292">
            {language === 'es' ? 'Inscríbete Ahora' : 'Enroll Now'}
          </a>
        </motion.div>
      </div>
    </section>;
}